import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Resolve Draft Details (OMS v3).
 *
 * Resolves the org-scoped details of a draft sales order in dependency order:
 * customer → products → ship-to location. Runs in the order's **selling org**,
 * after the routing org has classified + reassigned the draft (target-org
 * intake, before Split), so every lookup is scoped to the org that owns the
 * customers/products/locations. The routing-org Create Draft step deliberately
 * leaves these unresolved — matching there would scope to the wrong org.
 *
 * Customer + ship-to are matched by **deterministic pg_trgm** (customer name +
 * ship-to address) against the live DB, restricted to the draft's org — no
 * Pinecone/embeddings dependency, and no chance of binding across orgs.
 *
 * Idempotent: skips customer resolution when a customer is already set and
 * ship-to matching when a location is already set, so re-runs don't clobber a
 * resolved (or manually corrected) value.
 *
 * Backend-only (DB trigram matching); handler in
 * backend/src/workflows/steps/order-management/.
 */
export const resolveDraftDetailsStep = defineStepConfig({
  type: "resolve_draft_details",
  name: "Resolve Draft Details",
  description:
    "Resolve a draft sales order's customer, line-item products, and ship-to location (org-scoped)",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    draftSalesOrderId: z
      .string()
      .uuid()
      .describe("The draft sales order to resolve details for"),
  }),
  outputSchema: z.object({
    draftSalesOrderId: z.string().uuid(),
    customerId: z
      .string()
      .uuid()
      .nullable()
      .describe("Resolved customer ID, or null if no confident match"),
    confidence: z
      .enum(["high", "medium", "low", "none"])
      .describe("Confidence of the customer resolution"),
    shipToLocationId: z
      .string()
      .uuid()
      .nullable()
      .describe("Matched ship-to location ID, or null if none matched"),
  }),
  configSchema: z.object({
    matchProducts: z
      .boolean()
      .optional()
      .describe(
        "Match line items to products (CustomerProduct + ProductMatchingService). Default true.",
      ),
    matchShipToLocation: z
      .boolean()
      .optional()
      .describe(
        "Match the order's destination address to a ship-to location (re-run fallback; the customer match already resolves it). Default true.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "3 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
